import * as React from 'react';
import axios from 'axios';
import { useState, useEffect } from 'react';

import { Backdrop, CircularProgress } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, TextField, Select, MenuItem, Button, DialogActions, Typography, Box, InputLabel, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import PlacaVehiculoInput from './PlacaVehiculoInput';
import PlacaTrailerInput from './PlacaTrailerInput';
import PlacaMotoInput from './PlacaMotoInput';
import {
  combinePlates,
  getVehicleEditErrorMessage,
  normalizeClientIds,
  splitCombinedPlate,
} from '../../../../utils/vehicleEditing';
import { getVehicleYears } from '../../../../utils/vehicleYears';

const tipos = ['Automovil', 'Motocicleta', 'Camión', 'Camioneta', 'Trailer', 'Contenedor', 'Trailer + Cabezote', 'Cabezote', 'Volco', 'Otro'];
const marcas = ["Peterbilt", "Daf", "Isuzu", "Foton", "Iveco", "Scania", "Hino", "Freightliner", "Kodia", "International", "ProStar", "Mack", "Kenworth", "Eagle", "Chevrolet", "Renault", "Mazda", "Toyota", "Hyundai", "Kia", "Ford", "Volkswagen", "Nissan", "Honda", "Suzuki", "Jeep", "Subaru", "BMW", "Mercedes-Benz", "Audi", "Peugeot", "Fiat", "Mitsubishi", "Land Rover", "Jaguar", "Volvo", "Mini", "Dodge", "BYD", "JAC", "SsangYong", "Otro"];
const colores = ["Blanco", "Negro", "Gris", "Plata", "Rojo", "Azul", "Verde", "Amarillo", "Marrón", "Beige", "Naranja", "Violeta"];

const formatearClientes = (clientes) => {
  if (!clientes || clientes.length === 0) return ""; // Si no hay clientes

  const nombres = clientes.map((cliente) => cliente.nombre);

  if (nombres.length === 1) {
    return `${nombres[0]}`;
  }

  const ultimaParte = `y ${nombres[nombres.length - 1]}`;
  const nombresFormateados = nombres.slice(0, -1).join(", ");
  
  return `${nombresFormateados} ${ultimaParte}`;
};

const API_URL = process.env.REACT_APP_API_URL;

export default function DialogModifyVehiculo({ openDialog, handleCloseDialog, setOpenAlert, data, handleUpdateData }) {
  const modelos = getVehicleYears();
  const [placa, setPlaca] = useState("");
  const [placaMessage, setPlacaMessage] = useState("");
  const [tipoVehiculo, setTipoVehiculo] = useState("");
  const [modelo, setModelo] = useState("");
  const [marca, setMarca] = useState("");
  const [color, setColor] = useState("");
  const [combinedPlates, setCombinedPlates] = useState({ plateT: '', plateABC: '' });

  const [loading, setLoading] = useState(false);
  const submitRef = React.useRef(false);

  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({
    placa: false,
    tipoVehiculo: false,
    modelo: false,
    marca: false,
    color: false,
  });
  const plateResetKey = `${data?.id || ''}:${openDialog}:${tipoVehiculo}`;

  useEffect(() => {
    if (data) {
      setPlaca(data.placa || "");
      setPlacaMessage(data.placa || "");
      setTipoVehiculo(data.tipo_vehiculo || "");
      setModelo(data.modelo || "");
      setMarca(data.marca || "");
      setColor(data.color || "");
      setCombinedPlates(splitCombinedPlate(data.placa));
    }
  }, [data, openDialog]);

  const validateFields = () => {
    const errors = {
      placa: placa.trim() === "",
      tipoVehiculo: tipoVehiculo === "",
      modelo: modelo === "",
      marca: marca === "",
      color: color === "",
    };
    setFieldErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = async () => {
    if (submitRef.current || loading) return;
    if (!validateFields()) {
      setError("Por favor, complete todos los campos.");
      return;
    }
    submitRef.current = true;
    setLoading(true);

    const updatedData = {
      id: data.id, // Incluye el ID para identificar el objeto a actualizar
      placa,
      tipo_vehiculo: tipoVehiculo,
      modelo,
      marca,
      color,
      cliente_ids: normalizeClientIds(data.clientes),
    };

    try {
      const response = await axios.put(`${API_URL}/historial/${data.id}/`, updatedData);
      if (response.status === 200) {
        handleUpdateData(response.data); // Envía los datos actualizados al componente padre
        setLoading(false)
        handleCloseDialog();
        setOpenAlert(true);
      } else {
        setLoading(false)
        throw new Error("Error al actualizar los datos");
      }
    } catch (err) {
      setError(getVehicleEditErrorMessage(err));
    } finally {
      submitRef.current = false;
      setLoading(false);
    }
  }

  const handleCombinedPlates = (type, value) => {
    setCombinedPlates((prevPlates) => {
      const newPlates = { ...prevPlates, [type]: value };
      const combined = combinePlates(newPlates);
      if (combined) setPlaca(combined);
      return newPlates;
    });
  };

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        sx={{
          '& .MuiPaper-root': {
            width: { xs: '95%', sm: '85%', md: '60%' },
            maxWidth: 'none',
          },
        }}
      >
        <Backdrop
          open={loading}
          style={{ zIndex: 9999, color: '#fff' }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <DialogTitle>Modificar la Información del Vehículo</DialogTitle>
        <DialogContent>

          
          <InputLabel>Tipo</InputLabel>
          <Select
            fullWidth
            value={tipoVehiculo}
            error={fieldErrors.tipoVehiculo}
            onChange={(e) => {
              setTipoVehiculo(e.target.value);
              setCombinedPlates({ plateT: '', plateABC: '' });
            }}
          >
            {tipos.map((tipo) => (
              <MenuItem key={tipo} value={tipo}>
                {tipo}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.tipoVehiculo && <Typography color="error">Este campo es obligatorio</Typography>}

              <InputLabel sx={{ marginTop: 2 }} variant='h4'>Placa del Vehiculo</InputLabel>
              {tipoVehiculo === 'Trailer' || tipoVehiculo === 'Volco' ? (
                <PlacaTrailerInput 
                  defaultValue={placa}
                  resetKey={plateResetKey}
                  onComplete={(value) => setPlaca(value.toUpperCase())} 
                  error={fieldErrors.placa} 
                  disabled={false}
                />
              ) : tipoVehiculo === 'Motocicleta' ? (
                <PlacaMotoInput 
                  defaultValue={placa}
                  resetKey={plateResetKey}
                  onComplete={(value) => setPlaca(value.toUpperCase())} 
                  error={fieldErrors.placa} 
                  disabled={false}
                />
              ) : tipoVehiculo === 'Trailer + Cabezote' ? (
                <>
                  <PlacaVehiculoInput 
                    defaultValue={combinedPlates.plateABC}
                    resetKey={`${plateResetKey}:cabezote`}
                    onComplete={(value) => handleCombinedPlates('plateABC', value)} 
                    error={fieldErrors.placa} 
                    disabled={false}
                  />
                  <PlacaTrailerInput 
                    defaultValue={combinedPlates.plateT}
                    resetKey={`${plateResetKey}:trailer`}
                    onComplete={(value) => handleCombinedPlates('plateT', value)} 
                    error={fieldErrors.placa} 
                    disabled={false}
                  />
                </>
              ) : (
                <PlacaVehiculoInput 
                  defaultValue={placa}
                  resetKey={plateResetKey}
                  onComplete={(value) => setPlaca(value.toUpperCase())} 
                  error={fieldErrors.placa} 
                />
              )}
          <Typography>Placa original: {placaMessage}</Typography>
          

          <InputLabel>Modelo</InputLabel>
          <Select
            fullWidth
            value={modelo}
            error={fieldErrors.modelo}
            onChange={(e) => setModelo(e.target.value)}
          >
            {modelos.map((modelo) => (
              <MenuItem key={modelo} value={modelo}>
                {modelo}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.modelo && <Typography color="error">Este campo es obligatorio</Typography>}

          <InputLabel>Marca</InputLabel>
          <Select
            fullWidth
            value={marca}
            error={fieldErrors.marca}
            onChange={(e) => setMarca(e.target.value)}
          >
            {marcas.map((marca) => (
              <MenuItem key={marca} value={marca}>
                {marca}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.marca && <Typography color="error">Este campo es obligatorio</Typography>}

          <InputLabel>Color</InputLabel>
          <Select
            fullWidth
            value={color}
            error={fieldErrors.color}
            onChange={(e) => setColor(e.target.value)}
          >
            {colores.map((color) => (
              <MenuItem key={color} value={color}>
                {color}
              </MenuItem>
            ))}
          </Select>
          {fieldErrors.color && <Typography color="error">Este campo es obligatorio</Typography>}

          <InputLabel>Los clientes de este Vehiculo se modifican en la zona de Clientes: {formatearClientes(data.cliente_ids)}</InputLabel>
        </DialogContent>

        {error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: "#d32f2f",
                padding: 1,
                borderRadius: 1,
                width: '95%',
              }}
            >
              <Typography sx={{ color: "white", fontWeight: 'bold', marginRight: 1 }}>
                {error}
              </Typography>
              <IconButton color="inherit" onClick={() => setError("")}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        )}

        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            color='error'
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained" disabled={loading}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
